import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, CreditCard, Landmark, Loader2, FileDown, PenLine, Eraser, ShieldCheck, ExternalLink, Search } from "lucide-react";

const STATUS_LABEL = {
  brouillon: "Brouillon",
  envoye: "Envoyé",
  signe: "Signé",
  paye_acompte: "Acompte payé",
  en_cours: "En cours",
  livre: "Livré",
  paye_solde: "Soldé",
  cloture: "Clôturé",};

function frDate(iso) {
  if (!iso) return "";
  try {
    return new Date(String(iso).slice(0, 10)).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  useEffect(() => {
    const c = ref.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr); ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.lineWidth = 2.5; ctx.strokeStyle = "#0A1128";
  }, []);

  const getPos = (e) => {
    const r = ref.current.getBoundingClientRect();
    if (e.touches && e.touches[0]) return { x: e.touches[0].clientX - r.left, y: e.touches[0].clientY - r.top };
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; if (ref.current) onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { const c = ref.current; const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); onChange(""); };

  return (
    <div className="bg-white rounded-xl border-2 border-dashed border-[#06B6D4]/40 p-2">
      <canvas
        ref={ref}
        data-testid="signature-canvas"
        className="w-full h-44 touch-none rounded-lg"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-[#64748B] flex items-center gap-1"><PenLine size={12} /> Signez avec votre souris ou votre doigt</span>
        <button onClick={clear} data-testid="signature-clear" className="text-xs text-[#06B6D4] flex items-center gap-1 hover:underline"><Eraser size={12} />Effacer</button>
      </div>
    </div>
  );
}

export default function PublicProposal() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [sig, setSig] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [signing, setSigning] = useState(false);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [signerFirst, setSignerFirst] = useState("");
  const [signerLast, setSignerLast] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signerPhone, setSignerPhone] = useState("");
  const [signerRole, setSignerRole] = useState("");  // qualité du signataire (Gérant, Président, …)
  const [signedPlace, setSignedPlace] = useState("");
  // Company auto-loaded from SIRET registry (recherche-entreprises.api.gouv.fr)
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null); // {name, siret, siren, address, legal_form, tva}
  const [ysOpen, setYsOpen] = useState(false);
  const [ysUrl, setYsUrl] = useState("");
  const [ysInitiating, setYsInitiating] = useState(false);

  const load = () => api.get(`/proposals/public/${token}`).then((r) => {
    setData(r.data);
    const p = r.data.proposal;
    setSignedBy(p.signed_by || p.client?.name || "");
    // Pre-fill signer fields from client info
    const fullName = (p.client?.name || "").trim().split(/\s+/);
    if (!signerFirst && fullName.length) setSignerFirst(fullName[0]);
    if (!signerLast && fullName.length > 1) setSignerLast(fullName.slice(1).join(" "));
    if (!signerEmail) setSignerEmail(p.client?.email || "");
    if (!signerPhone) setSignerPhone(p.client?.phone || "");
    if (!companyQuery) setCompanyQuery(p.client?.company || "");
  }).catch(() => toast.error("Devis introuvable"));

  // Auto-search company when the user types a SIRET (14 digits) or > 4 chars (debounced)
  useEffect(() => {
    if (selectedCompany) return;
    const q = companyQuery.replace(/\s/g, "");
    if (!q || q.length < 4) { setCompanyResults([]); return; }
    const t = setTimeout(async () => {
      setCompanyLoading(true);
      try {
        const isSiret = /^\d{14}$/.test(q);
        const isSiren = /^\d{9}$/.test(q);
        const params = isSiret ? { siret: q } : isSiren ? { siren: q } : { q: companyQuery.trim() };
        const r = await api.get("/portal/company-lookup", { params });
        setCompanyResults(r.data.results || []);
      } catch { setCompanyResults([]); }
      finally { setCompanyLoading(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [companyQuery, selectedCompany]);

  const pickCompany = (c) => {
    const addressParts = [c.adresse, c.code_postal, c.commune].filter(Boolean).join(" ");
    setSelectedCompany({
      name: c.nom_complet || c.denomination || c.nom_raison_sociale || "",
      siret: c.siret || "",
      siren: c.siren || "",
      tva: c.siren ? `FR${(76 - 9 * parseInt(c.siren, 10)) % 97}`.padStart(13, "0").replace(/(.{4})/, "FR$1") : "",
      address: addressParts,
      legal_form: c.forme_juridique || "",
    });
    setCompanyResults([]);
    // Default place = commune of the registered office
    if (!signedPlace && c.commune) setSignedPlace(c.commune);
  };

  const clearCompany = () => { setSelectedCompany(null); setCompanyResults([]); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  // Poll Yousign status when the signature modal is open (in case webhook isn't configured)
  useEffect(() => {
    if (!ysOpen || !data) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/proposals/public/${token}/yousign/status`);
        if (r.data?.status === "done") {
          toast.success("Signature reçue !");
          setYsOpen(false);
          load();
          return;
        }
      } catch {}
      if (!cancelled) setTimeout(tick, 5000);
    };
    setTimeout(tick, 4000);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ysOpen, token]);

  // Stripe success polling
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !data) return;
    setPolling(true);
    let n = 0;
    const tick = async () => {
      try { await api.get(`/checkout/status/${sid}`); } catch {}
      load();
      n++;
      if (n >= 5) { setPolling(false); return; }
      setTimeout(tick, 2000);
    };
    tick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, token]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#64748B]"><Loader2 className="animate-spin mr-2" /> Chargement...</div>;

  const p = data.proposal;
  const company = data.company;
  const oneOff = (p.items || []).filter((i) => !i.is_recurring);
  const recurringMonthly = (p.items || []).filter((i) => i.is_recurring && (i.billing_cycle || "month") === "month");
  const recurringYearly = (p.items || []).filter((i) => i.is_recurring && i.billing_cycle === "year");
  const total = (p.totals || {}).total_ttc || 0;
  const deposit = p.payment_terms === "100_upfront" ? total : Math.round(total * 0.5 * 100) / 100;

  const sign = async () => {
    if (!sig) return toast.error("Veuillez signer dans la zone");
    if (!signerFirst || !signerLast) return toast.error("Veuillez renseigner vos prénom et nom");
    if (!selectedCompany) return toast.error("Sélectionnez l'entreprise signataire (via SIRET ou nom)");
    if (!signedPlace) return toast.error("Veuillez indiquer le lieu (ville) de la signature");
    if (!cgvAccepted) return toast.error("Vous devez accepter les CGV pour signer");
    const fullName = `${signerFirst} ${signerLast}`.trim();
    setSigning(true);
    try {
      await api.post(`/proposals/public/${token}/sign`, {
        signature_dataurl: sig,
        signed_by: fullName,
        signed_first_name: signerFirst,
        signed_last_name: signerLast,
        signed_place: signedPlace,
        cgv_accepted: cgvAccepted,
        company_name: selectedCompany.name,
        company_siret: selectedCompany.siret,
        company_siren: selectedCompany.siren,
        company_tva: selectedCompany.tva,
        company_address: selectedCompany.address,
        company_legal_form: selectedCompany.legal_form,
        company_role: signerRole || null,
        signer_phone: signerPhone || null,
        signer_email: signerEmail || null,
      });
      toast.success("Devis signé !");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSigning(false); }
  };

  const initYousign = async () => {
    if (!cgvAccepted) return toast.error("Vous devez accepter les CGV pour signer");
    if (!signerFirst || !signerLast) return toast.error("Renseignez vos prénom et nom");
    if (!signerEmail) return toast.error("Renseignez votre email");
    if (!selectedCompany) return toast.error("Sélectionnez l'entreprise signataire (via SIRET ou nom)");
    if (!signedPlace) return toast.error("Indiquez le lieu (ville) où vous signez");
    setYsInitiating(true);
    try {
      const r = await api.post(`/proposals/public/${token}/yousign/init`, {
        signer_first_name: signerFirst,
        signer_last_name: signerLast,
        signer_email: signerEmail,
        cgv_accepted: cgvAccepted,
        signed_place: signedPlace,
        signer_phone: signerPhone || null,
        company_name: selectedCompany.name,
        company_siret: selectedCompany.siret,
        company_siren: selectedCompany.siren,
        company_tva: selectedCompany.tva,
        company_address: selectedCompany.address,
        company_legal_form: selectedCompany.legal_form,
        company_role: signerRole || null,
      });
      setYsUrl(r.data.signing_url);
      setYsOpen(true);
      window.open(r.data.signing_url, "_blank", "noopener,noreferrer");
      toast.success("Page de signature Yousign ouverte dans un nouvel onglet");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Yousign");
    } finally { setYsInitiating(false); }
  };

  const pay = async (mode) => {
    setPaying(true);
    try {
      const r = await api.post(`/proposals/public/${token}/checkout`, { origin_url: window.location.origin, mode });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setPaying(false); }
  };

  const downloadPdf = () => {
    // Use ?download=1 so Content-Disposition forces 'attachment' with the correct filename
    window.open(`${window.location.origin}/api/proposals/public/${token}/pdf?download=1`, "_blank");
  };

  const signed = ["signe", "paye_acompte", "en_cours", "livre", "paye_solde", "cloture"].includes(p.status);
  const depositPaid = ["paye_acompte", "en_cours", "livre", "paye_solde", "cloture"].includes(p.status);

  return (
    <div data-testid="public-proposal" className="min-h-screen bg-[#FAFAFA] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Top header */}
        <div className="bg-[#0A1128] text-white rounded-3xl p-6 sm:p-8 relative overflow-hidden grain">
          <div className="absolute -top-20 -right-20 w-72 h-72 blob-turquoise opacity-30" />
          <div className="relative flex items-start justify-between flex-wrap gap-3">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_business-mvp-1/artifacts/c6kygdj0_BESSON-footer.png" className="w-56 mb-3" alt="BESSON SOLUTIONS" />
              <div className="text-xs tracking-[0.3em] uppercase text-[#67E8F9]">Proposition commerciale</div>
              <div className="display text-3xl sm:text-4xl font-extrabold mt-1">Devis {p.ref}</div>
              <div className="text-white/70 text-sm mt-1">Émis le {frDate(p.created_at)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-white/60">Statut</div>
              <div className="display text-xl font-extrabold mt-1">{STATUS_LABEL[p.status] || p.status}</div>
              <Button onClick={downloadPdf} data-testid="public-pdf" size="sm" className="mt-3 bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9]"><FileDown size={14} className="mr-1" />Télécharger PDF</Button>
            </div>
          </div>
        </div>

        {/* Client + Project */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white border border-black/8 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold">Client</div>
            <div className="display font-bold text-[#0A1128] mt-2 text-lg">{p.client.name}</div>
            {p.client.company && <div className="text-sm text-[#475569]">{p.client.company}</div>}
            <div className="text-sm text-[#64748B] mt-1">{p.client.email}{p.client.phone ? ` · ${p.client.phone}` : ""}</div>
          </div>
          <div className="bg-white border border-black/8 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold">Projet</div>
            <div className="display font-bold text-[#0A1128] mt-2 text-lg">{p.project.title}</div>
            {p.project.deadline && <div className="text-sm text-[#475569] mt-1"><b>Délai :</b> {p.project.deadline}</div>}
          </div>
        </div>

        {p.project.brief && (
          <div className="mt-4 bg-white border border-black/8 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold mb-2">Brief / contexte</div>
            <div className="text-[#0A1128] whitespace-pre-wrap leading-relaxed">{p.project.brief}</div>
          </div>
        )}

        {(p.project.objectives || []).length > 0 && (
          <div className="mt-4 bg-white border border-black/8 rounded-2xl p-5">
            <div className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold mb-2">Objectifs</div>
            <ul className="space-y-1.5">
              {p.project.objectives.map((o, i) => (
                <li key={i} className="flex items-start gap-2 text-[#0A1128]"><CheckCircle2 size={16} className="text-[#06B6D4] mt-0.5 flex-shrink-0" /> {o}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Items */}
        {oneOff.length > 0 && (
          <ItemsCard title="Prestations ponctuelles" items={oneOff} subtotalLabel="Sous-total HT" subtotalHT={p.totals?.subtotal_ht} />
        )}
        {recurringMonthly.length > 0 && (
          <ItemsCard title="Prestations récurrentes — mensuelles" items={recurringMonthly} recurring cycle="month" subtotalLabel="Sous-total HT /Mois" subtotalHT={p.totals?.recurring_monthly_ht} />
        )}
        {recurringYearly.length > 0 && (
          <ItemsCard title="Prestations récurrentes — annuelles" items={recurringYearly} recurring cycle="year" subtotalLabel="Sous-total HT /An" subtotalHT={p.totals?.recurring_yearly_ht} />
        )}

        {/* Totals */}
        <div className="mt-4 bg-white border border-black/8 rounded-2xl p-6 shadow-sm">
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div className="text-[#64748B]">Total HT (prestations ponctuelles)</div>
            <div className="text-right font-semibold text-[#0A1128] tabular-nums">{(p.totals.subtotal_ht || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
            {(p.totals.discount_amount_ht || 0) > 0 && (
              <>
                <div className="text-[#92400E]">Remise commerciale{p.totals.discount_kind === "percent" && p.totals.discount_value ? ` (${p.totals.discount_value}%)` : ""}</div>
                <div className="text-right font-semibold text-[#92400E] tabular-nums">-{(p.totals.discount_amount_ht || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
                <div className="text-[#64748B]">Total HT remisé</div>
                <div className="text-right font-semibold text-[#0A1128] tabular-nums">{(p.totals.subtotal_after_discount_ht || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
              </>
            )}
            <div className="text-[#64748B]">TVA</div>
            <div className="text-right font-semibold text-[#0A1128] tabular-nums">{(p.totals.vat || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
          </div>
          <div className="mt-3 pt-3 border-t border-black/10 flex items-center justify-between">
            <span className="display text-xl font-extrabold text-[#0A1128]">Total TTC</span>
            <span className="display text-3xl font-extrabold text-[#06B6D4] tabular-nums">{total.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span>
          </div>
          {p.totals.recurring_monthly_ttc > 0 && <div className="mt-2 text-xs text-[#065F46] bg-[#A7F3D0]/40 inline-block px-2 py-0.5 rounded-full font-bold mr-2">+ {p.totals.recurring_monthly_ttc.toLocaleString("fr-FR")} € TTC /Mois</div>}
          {p.totals.recurring_yearly_ttc > 0 && <div className="mt-2 text-xs text-[#065F46] bg-[#A7F3D0]/40 inline-block px-2 py-0.5 rounded-full font-bold">+ {p.totals.recurring_yearly_ttc.toLocaleString("fr-FR")} € TTC /An</div>}

          {/* Payment schedule — light theme */}
          {p.payment_schedule?.installments?.length > 0 && (
            <div data-testid="public-schedule" className="mt-5 bg-[#FAFAFA] border border-black/8 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#06B6D4]" />
                <span className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold">Modalités de paiement</span>
              </div>
              <div className="text-sm text-[#0A1128] mb-3">{p.payment_schedule.summary}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm min-w-[420px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#64748B] border-b border-black/10">
                      <th className="text-left py-2">Échéance</th>
                      <th className="text-left py-2">Date</th>
                      <th className="text-right py-2">Montant</th>
                      <th className="text-right py-2">Solde dû</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.payment_schedule.installments.map((it) => (
                      <tr key={it.index} className="border-b border-black/5 last:border-0">
                        <td className="py-2 font-bold text-[#0A1128]">{it.label}</td>
                        <td className="py-2 text-[#64748B]">{it.due_at}</td>
                        <td className="py-2 text-right font-extrabold tabular-nums text-[#0A1128]">{Number(it.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</td>
                        <td className="py-2 text-right tabular-nums">
                          {Number(it.remaining) > 0
                            ? <span className="text-[#64748B]">{Number(it.remaining).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</span>
                            : <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#A7F3D0] text-[#065F46]">soldé</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Signature */}
        <div className="mt-6 bg-white border border-black/8 rounded-2xl p-6" data-testid="public-signature-block">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold flex items-center gap-1.5"><ShieldCheck size={14} />Signature électronique</div>
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#FFDCA8] text-[#92400E]">Yousign · Prochainement disponible</span>
          </div>
          {signed ? (
            <div className="bg-[#A7F3D0]/20 border-2 border-[#A7F3D0] rounded-2xl p-5" data-testid="public-signed-block">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="text-green-600" size={28} />
                <div>
                  <div className="display font-bold text-[#0A1128] text-lg">Devis accepté & signé</div>
                  <div className="text-xs text-[#64748B]">Conservez ce document pour vos archives.</div>
                </div>
              </div>
              {/* Entreprise signataire */}
              {p.signed_company?.name && (
                <div className="bg-white border border-[#A7F3D0] rounded-xl p-4 mb-3" data-testid="public-signed-company">
                  <div className="text-[10px] uppercase tracking-widest text-[#06B6D4] font-bold mb-1">Entreprise signataire</div>
                  <div className="font-bold text-[#0A1128]">{p.signed_company.name}</div>
                  <div className="text-xs text-[#64748B] grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 mt-1.5">
                    {p.signed_company.siret && <div><span className="font-bold">SIRET :</span> {p.signed_company.siret}</div>}
                    {p.signed_company.legal_form && <div><span className="font-bold">Forme :</span> {p.signed_company.legal_form}</div>}
                    {p.signed_company.tva && <div><span className="font-bold">TVA :</span> {p.signed_company.tva}</div>}
                    {p.signed_company.role && <div><span className="font-bold">Qualité signataire :</span> {p.signed_company.role}</div>}
                    {p.signed_company.address && <div className="col-span-full"><span className="font-bold">Adresse :</span> {p.signed_company.address}</div>}
                  </div>
                </div>
              )}
              {/* Coordonnées signataire */}
              <div className="bg-white border border-[#A7F3D0] rounded-xl p-4 mb-3" data-testid="public-signed-contact">
                <div className="text-[10px] uppercase tracking-widest text-[#06B6D4] font-bold mb-1">Coordonnées du signataire</div>
                <div className="font-bold text-[#0A1128]">{p.signed_first_name || ""} {p.signed_last_name || ""}</div>
                <div className="text-xs text-[#64748B] mt-0.5">
                  {p.signer_contact?.email && <span className="mr-3">{p.signer_contact.email}</span>}
                  {p.signer_contact?.phone && <span>{p.signer_contact.phone}</span>}
                </div>
              </div>
              {/* Date & Lieu */}
              <div className="grid grid-cols-2 gap-3 bg-white border border-[#A7F3D0] rounded-xl p-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">Date</div>
                  <div className="text-sm font-bold text-[#0A1128]">{frDate(p.signed_at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">Fait à</div>
                  <div className="text-sm font-bold text-[#0A1128]">{p.signed_place || "—"}</div>
                </div>
              </div>
              <div className="mt-3 bg-white border border-[#A7F3D0] rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1">Signature & mention « Bon pour accord »</div>
                {p.signature_dataurl ? (
                  <img src={p.signature_dataurl} alt="Signature du client" className="max-w-xs border border-black/10 rounded bg-[#FAFAFA] p-2" />
                ) : (
                  <div className="text-sm text-[#0A1128] italic">Bon pour accord — {p.signed_by}</div>
                )}
                {p.signature_provider === "yousign" && <div className="text-[10px] uppercase tracking-widest text-[#06B6D4] font-bold mt-2 flex items-center gap-1"><ShieldCheck size={11} />Signature électronique certifiée Yousign</div>}
              </div>
            </div>
          ) : ysOpen ? (
            <div className="bg-[#FAFAFA] border border-black/10 rounded-xl p-5 text-center" data-testid="public-ys-pending">
              <Loader2 className="animate-spin mx-auto text-[#06B6D4]" size={28} />
              <div className="display font-bold text-[#0A1128] mt-3">Signature en cours sur Yousign…</div>
              <div className="text-sm text-[#64748B] mt-1">L&apos;onglet Yousign s&apos;est ouvert. Une fois la signature complétée, cette page se mettra à jour automatiquement.</div>
              {ysUrl && (
                <a href={ysUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-sm text-[#06B6D4] font-bold hover:underline" data-testid="public-ys-reopen">
                  <ExternalLink size={14} />Rouvrir l&apos;onglet de signature
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              {/* ====== ENTREPRISE SIGNATAIRE ====== */}
              <div className="bg-[#FAFAFA] border border-black/8 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#06B6D4] mb-1">Entreprise signataire</div>
                <p className="text-xs text-[#64748B] mb-3">Recherchez votre entreprise par <strong>SIRET (14 chiffres)</strong>, <strong>SIREN (9 chiffres)</strong> ou <strong>nom commercial</strong>. Les informations légales seront automatiquement chargées depuis le registre des entreprises.</p>
                {!selectedCompany ? (
                  <>
                    <div className="relative">
                      <Input
                        data-testid="public-company-search"
                        value={companyQuery}
                        onChange={(e) => setCompanyQuery(e.target.value)}
                        placeholder="Ex: 12345678901234 ou BESSON SOLUTIONS"
                        className="pl-9"
                      />
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                      {companyLoading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#06B6D4]" />}
                    </div>
                    {companyResults.length > 0 && (
                      <div className="mt-2 border border-black/8 rounded-xl bg-white max-h-72 overflow-y-auto" data-testid="public-company-results">
                        {companyResults.map((c, i) => (
                          <button
                            key={c.siret || c.siren || i}
                            type="button"
                            onClick={() => pickCompany(c)}
                            className="w-full text-left px-3 py-2.5 border-b border-black/5 last:border-0 hover:bg-[#06B6D4]/8 transition-colors"
                            data-testid={`public-company-pick-${i}`}
                          >
                            <div className="font-bold text-[#0A1128] text-sm truncate">{c.nom_complet || c.denomination}</div>
                            <div className="text-[11px] text-[#64748B] truncate">SIRET {c.siret || "—"} · {c.forme_juridique || "—"} · {c.commune || "—"}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white border border-[#06B6D4]/40 rounded-xl p-3 space-y-1.5" data-testid="public-company-selected">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[#0A1128] text-sm truncate">{selectedCompany.name}</div>
                        <div className="text-[11px] text-[#64748B] grid grid-cols-2 gap-x-3 gap-y-0.5 mt-1.5">
                          <div><span className="font-bold">SIRET :</span> {selectedCompany.siret || "—"}</div>
                          <div><span className="font-bold">Forme :</span> {selectedCompany.legal_form || "—"}</div>
                          <div className="col-span-2"><span className="font-bold">Adresse :</span> {selectedCompany.address || "—"}</div>
                          {selectedCompany.tva && <div><span className="font-bold">TVA :</span> {selectedCompany.tva}</div>}
                        </div>
                      </div>
                      <button type="button" onClick={clearCompany} data-testid="public-company-clear" className="text-[10px] text-red-600 hover:underline font-bold whitespace-nowrap">Changer</button>
                    </div>
                  </div>
                )}
              </div>

              {/* ====== COORDONNÉES DU SIGNATAIRE ====== */}
              <div className="bg-[#FAFAFA] border border-black/8 rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#06B6D4] mb-1">Coordonnées du signataire</div>
                <p className="text-xs text-[#64748B] mb-3">La personne physique qui signe au nom de l&apos;entreprise.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Prénom *</Label><Input data-testid="public-signer-first" value={signerFirst} onChange={(e) => setSignerFirst(e.target.value)} placeholder="Jean" /></div>
                  <div><Label>Nom *</Label><Input data-testid="public-signer-last" value={signerLast} onChange={(e) => setSignerLast(e.target.value)} placeholder="Dupont" /></div>
                  <div><Label>Email *</Label><Input type="email" data-testid="public-signer-email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder="jean@entreprise.com" /></div>
                  <div><Label>Téléphone</Label><Input type="tel" data-testid="public-signer-phone" value={signerPhone} onChange={(e) => setSignerPhone(e.target.value)} placeholder="06 12 34 56 78" /></div>
                  <div><Label>Qualité (Gérant, Président…)</Label><Input data-testid="public-signer-role" value={signerRole} onChange={(e) => setSignerRole(e.target.value)} placeholder="Gérant" /></div>
                  <div><Label>Fait à (ville) *</Label><Input data-testid="public-signed-place" value={signedPlace} onChange={(e) => setSignedPlace(e.target.value)} placeholder="Paris" /></div>
                </div>
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer bg-[#FAFAFA] border border-black/10 rounded-xl p-3" data-testid="public-cgv-label">
                <input
                  type="checkbox"
                  data-testid="public-cgv-checkbox"
                  checked={cgvAccepted}
                  onChange={(e) => setCgvAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#06B6D4] flex-shrink-0"
                />
                <span className="text-sm text-[#0A1128]">
                  Je reconnais avoir <b>consulté les <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-[#06B6D4] font-bold underline">Conditions Générales de Vente</a></b> publiées sur le site BESSON SOLUTIONS et les <b>accepte sans condition</b>. Mon acceptation engage l'entreprise signataire.
                </span>
              </label>

              <div className="w-full bg-gradient-to-r from-[#FFDCA8]/30 to-[#A7F3D0]/30 border border-[#FFDCA8]/50 rounded-xl px-4 py-3 text-center text-sm text-[#0A1128] mb-3" data-testid="public-yousign-coming-soon">
                <div className="font-extrabold text-[#92400E]">✨ Signature certifiée Yousign — Prochainement disponible</div>
                <div className="text-xs text-[#64748B] mt-1">En attendant, vous pouvez signer manuellement ci-dessous. Cette signature engage juridiquement l'entreprise au même titre.</div>
              </div>

              <div className="space-y-3">
                <div className="bg-[#FFDCA8]/15 border border-[#FFDCA8]/40 rounded-lg p-2 text-[11px] text-[#92400E]">
                  Tracez votre signature ci-dessous. L&apos;entreprise, le signataire, la date et le lieu seront automatiquement apposés sur le devis signé.
                </div>
                <SignaturePad onChange={setSig} />
                <Button onClick={sign} disabled={signing || !cgvAccepted} data-testid="public-sign-btn" className="w-full bg-[#0A1128] hover:bg-[#14213d] text-white disabled:opacity-60">
                  {signing ? <><Loader2 className="animate-spin mr-2" size={14} />Signature en cours...</> : <><PenLine size={14} className="mr-2" />Signer électroniquement le devis</>}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Payment after signature */}
        {signed && !depositPaid && (
          <div className="mt-6 bg-white border border-black/8 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-widest text-[#06B6D4] font-bold mb-2">Paiement {p.payment_terms === "100_upfront" ? "intégral" : "de l'acompte (50%)"}</div>
            <div className="display text-3xl font-extrabold text-[#0A1128]">{deposit.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</div>
            <div className="text-sm text-[#64748B] mt-1">Choisissez votre mode de paiement :</div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <button data-testid="public-pay-stripe" onClick={() => pay(p.payment_terms === "100_upfront" ? "full" : "deposit")} disabled={paying} className="text-left bg-[#0A1128] text-white rounded-2xl p-5 hover:bg-[#14213d] transition-colors disabled:opacity-50">
                <CreditCard size={22} className="text-[#67E8F9]" />
                <div className="display font-bold mt-2">Carte bancaire</div>
                <div className="text-xs text-white/65 mt-1">Paiement sécurisé Stripe — instantané</div>
              </button>
              <div className="bg-[#FAFAFA] border border-black/8 rounded-2xl p-5">
                <Landmark size={22} className="text-[#0A1128]" />
                <div className="display font-bold text-[#0A1128] mt-2">Virement bancaire</div>
                <div className="text-xs text-[#64748B] mt-1">Référence à mettre : <b>{p.ref}</b></div>
                <div className="mt-3 text-xs space-y-1 bg-white p-3 rounded-lg border border-black/8">
                  <div><b>Titulaire</b> : {company.bank_holder || company.legal_name || "—"}</div>
                  <div className="break-all"><b>IBAN</b> : {company.iban || "à venir"}</div>
                  <div><b>BIC</b> : {company.bic || "à venir"}</div>
                </div>
              </div>
            </div>
            {polling && <div className="mt-3 text-xs text-[#64748B] flex items-center"><Loader2 className="animate-spin mr-2" size={12} /> Vérification du paiement...</div>}
          </div>
        )}

        {depositPaid && (
          <div className="mt-6 bg-[#A7F3D0]/30 border border-[#A7F3D0] rounded-2xl p-6 text-center">
            <CheckCircle2 className="text-green-600 mx-auto" size={36} />
            <div className="display text-xl font-extrabold text-[#0A1128] mt-2">Merci pour votre confiance !</div>
            <div className="text-sm text-[#475569] mt-1">Votre paiement est confirmé, nous démarrons le projet dès maintenant.</div>
          </div>
        )}

        <div className="mt-10 text-center text-xs text-[#64748B]">
          {company.legal_name || "BESSON SOLUTIONS"} · SIRET {company.siret || "—"} · {company.email}
        </div>
      </div>
    </div>
  );
}

function ItemsCard({ title, items, recurring, cycle, subtotalLabel, subtotalHT }) {
  const cycleLabel = cycle === "year" ? "/An" : cycle === "month" ? "/Mois" : null;
  return (
    <div className="mt-4 bg-white border border-black/8 rounded-2xl overflow-hidden">
      <div className={`px-5 py-3 ${recurring ? "bg-[#06B6D4]/10" : "bg-[#FAFAFA]"} border-b border-black/8 flex items-center justify-between`}>
        <div className="display font-bold text-[#0A1128] text-sm">{title}</div>
        {recurring && cycleLabel && <span className="text-[10px] px-2 py-0.5 bg-[#06B6D4] text-[#0A1128] rounded-full font-bold">Récurrent · {cycleLabel}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-[#64748B] border-b border-black/5"><tr><th className="text-left px-5 py-2">Désignation</th><th className="text-right px-2 py-2">Qté</th><th className="text-right px-2 py-2">PU HT</th><th className="text-right px-2 py-2">Remise</th><th className="text-right px-5 py-2">Total HT</th></tr></thead>
          <tbody>
            {items.map((it, i) => {
              const qty = parseFloat(it.qty) || 0;
              const pu = parseFloat(it.unit_price) || 0;
              const gross = qty * pu;
              const dk = it.discount_kind;
              const dv = parseFloat(it.discount_value) || 0;
              let lineDisc = 0;
              let discLabel = "—";
              if (dk === "percent" && dv > 0) { lineDisc = Math.round(gross * Math.min(100, dv) / 100 * 100) / 100; discLabel = `-${dv}%`; }
              else if (dk === "amount" && dv > 0) { lineDisc = Math.round(Math.min(dv, gross) * 100) / 100; discLabel = `-${lineDisc.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €`; }
              const netHT = gross - lineDisc;
              return (
                <tr key={i} className="border-b border-black/5 last:border-0">
                  <td className="px-5 py-3">
                    <div className="font-bold text-[#0A1128]">{it.name}</div>
                    {it.description && <div className="text-xs text-[#64748B]">{it.description}</div>}
                    {recurring && cycleLabel && <div className="text-xs text-[#06B6D4] font-bold mt-1">{cycleLabel}</div>}
                  </td>
                  <td className="px-2 py-3 text-right">{qty}</td>
                  <td className="px-2 py-3 text-right">{pu.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</td>
                  <td className={`px-2 py-3 text-right ${lineDisc > 0 ? "text-[#92400E] font-semibold" : "text-[#94a3b8]"}`}>{discLabel}</td>
                  <td className="px-5 py-3 text-right font-semibold">{netHT.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</td>
                </tr>
              );
            })}
          </tbody>
          {subtotalLabel && subtotalHT !== undefined && (
            <tfoot>
              <tr className="bg-[#FAFAFA] border-t border-black/8 text-[#0A1128]">
                <td colSpan={4} className="px-5 py-2 text-right font-bold uppercase tracking-widest text-xs">{subtotalLabel}</td>
                <td className="px-5 py-2 text-right font-extrabold tabular-nums">{Number(subtotalHT || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
